import { useCallback, useEffect, useMemo, useState } from "react";
import AppLayout from "../../../../shared/components/AppLayout";
import Button from "../../../../shared/components/Button";
import EmptyState from "../../../../shared/components/EmptyState";
import LoadingPage from "../../../../shared/components/LoadingPage";
import Toast from "../../../../shared/components/Toast";
import { siteTexts } from "../../../../shared/i18n/siteTexts";
import { buildLoginHref } from "../../../../shared/lib/loginRedirect";
import { buildOpenMatchShareUrl } from "../../../../shared/lib/shareLinks";
import { normalizeSearchText } from "../../../../shared/utils/searchText";
import { useAuth } from "../../../auth/shared/hooks/useAuth";
import { listClubs } from "../../../clubs/shared/services/clubsService";
import type { Club } from "../../../clubs/shared/types/club";
import MatchCard from "../components/MatchCard";
import MatchCreatePanel from "../components/MatchCreatePanel";
import MatchFiltersBar from "../components/MatchFiltersBar";
import MatchParticipantModal from "../components/MatchParticipantModal";
import {
  addMatchParticipant,
  cancelMyMatchParticipant,
  confirmMatch,
  createMatch,
  cancelMatch,
  listMyMatches,
  listPublicMatches,
  requestJoinMatch,
  reviewMatchParticipant,
  updateMatch,
} from "../../shared/services/matchesService";
import type {
  CreateOpenMatchPayload,
  OpenMatchInitialParticipant,
  OpenMatchGenderPreference,
  OpenMatchItem,
} from "../../shared/types/matches";
import {
  formatOpenMatchCategories,
  formatOpenMatchGenderPreference,
  parseOpenMatchDateTimeValue,
  resolveOpenMatchAvailableSlots,
  resolveOpenMatchLowestPerPlayerCents,
  resolveOpenMatchStartMinutes,
  sortOpenMatches,
} from "../../shared/utils/openMatchPresentation";
import { readMatchRouteState } from "../../shared/utils/matchRouteState";
import "./MatchesPage.css";

function categoryOptions() {
  return [
    { value: "7a", label: siteTexts.common.category.c7a },
    { value: "6a", label: siteTexts.common.category.c6a },
    { value: "5a", label: siteTexts.common.category.c5a },
    { value: "4a", label: siteTexts.common.category.c4a },
    { value: "3a", label: siteTexts.common.category.c3a },
    { value: "2a", label: siteTexts.common.category.c2a },
    { value: "open", label: siteTexts.common.category.open },
    { value: "free", label: siteTexts.common.category.free },
  ];
}

export default function MatchesPage() {
  const { token, user } = useAuth();
  const texts = siteTexts.openMatches;
  const [matches, setMatches] = useState<OpenMatchItem[]>([]);
  const [clubs, setClubs] = useState<Club[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingClubs, setLoadingClubs] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [showCreatePanel, setShowCreatePanel] = useState(false);
  const [editingMatch, setEditingMatch] = useState<OpenMatchItem | null>(null);
  const [participantModalMatch, setParticipantModalMatch] =
    useState<OpenMatchItem | null>(null);
  const [participantModalSaving, setParticipantModalSaving] = useState(false);
  const [participantActionId, setParticipantActionId] = useState<number | null>(
    null,
  );
  const [searchValue, setSearchValue] = useState("");
  const [dateValue, setDateValue] = useState("");
  const [clubValue, setClubValue] = useState("");
  const [cityValue, setCityValue] = useState("");
  const [categoryValue, setCategoryValue] = useState("");
  const [genderValue, setGenderValue] = useState<"" | OpenMatchGenderPreference>("");
  const [timeFromValue, setTimeFromValue] = useState("");
  const [timeToValue, setTimeToValue] = useState("");
  const [availableOnly, setAvailableOnly] = useState(false);
  const [sortValue, setSortValue] = useState("recent");
  const [toastMessage, setToastMessage] = useState("");
  const [routeState, setRouteState] = useState(() =>
    readMatchRouteState(window.location.hash),
  );

  const loadMatches = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const response = await listPublicMatches();
      const publicMatches = response.matches || [];
      if (!token) {
        setMatches(publicMatches);
        return;
      }
      const myResponse = await listMyMatches(token).catch(() => ({
        matches: [],
      }));
      const myMatchesById = new Map(
        (myResponse.matches || []).map((match) => [Number(match.id), match]),
      );
      setMatches(
        publicMatches.map((match) => ({
          ...match,
          ...(myMatchesById.get(Number(match.id)) || {}),
        })),
      );
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : texts.loadError);
    } finally {
      setLoading(false);
    }
  }, [texts.loadError, token]);

  useEffect(() => {
    void loadMatches();
  }, [loadMatches]);

  useEffect(() => {
    const syncRouteState = () => {
      setRouteState(readMatchRouteState(window.location.hash));
    };
    window.addEventListener("hashchange", syncRouteState);
    return () => {
      window.removeEventListener("hashchange", syncRouteState);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoadingClubs(true);
    listClubs({})
      .then((response) => {
        if (cancelled) return;
        setClubs(response.clubs || []);
      })
      .catch(() => {
        if (cancelled) return;
        setClubs([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingClubs(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setClubValue(routeState.prefilledClubId ? String(routeState.prefilledClubId) : "");
  }, [routeState.prefilledClubId]);

  useEffect(() => {
    if (routeState.shouldOpenCreate && token && !editingMatch) {
      setShowCreatePanel(true);
    }
  }, [editingMatch, routeState.shouldOpenCreate, token]);

  const cityOptions = useMemo(
    () =>
      Array.from(
        new Set(
          matches
            .map((match) => String(match.city || "").trim())
            .filter(Boolean),
        ),
      ).sort((left, right) => left.localeCompare(right, "pt-BR")),
    [matches],
  );

  const clubOptions = useMemo(
    () =>
      clubs
        .map((club) => ({
          value: String(club.id),
          label: club.name,
        }))
        .sort((left, right) => left.label.localeCompare(right.label, "pt-BR")),
    [clubs],
  );

  const genderOptions = useMemo(
    (): Array<{ value: OpenMatchGenderPreference; label: string }> => [
      { value: "female", label: formatOpenMatchGenderPreference("female") },
      { value: "male", label: formatOpenMatchGenderPreference("male") },
      { value: "mixed", label: formatOpenMatchGenderPreference("mixed") },
    ],
    [],
  );

  const clubsById = useMemo(() => {
    const nextMap = new Map<number, Club>();
    clubs.forEach((club) => {
      nextMap.set(Number(club.id), club);
    });
    return nextMap;
  }, [clubs]);

  const sortOptions = useMemo(
    () => [
      { value: "recent", label: texts.results.sortRecent },
      { value: "upcoming", label: texts.results.sortUpcoming },
      { value: "price", label: texts.results.sortLowestPrice },
      { value: "spots", label: texts.results.sortMostSpots },
    ],
    [
      texts.results.sortLowestPrice,
      texts.results.sortMostSpots,
      texts.results.sortRecent,
      texts.results.sortUpcoming,
    ],
  );

  const selectedSortOption = useMemo(
    () => sortOptions.find((option) => option.value === sortValue) ?? sortOptions[0],
    [sortOptions, sortValue],
  );

  const visibleMatches = useMemo(() => {
    const normalizedSearch = normalizeSearchText(searchValue);
    const timeFromMinutes = timeFromValue
      ? Number(timeFromValue.slice(0, 2)) * 60 + Number(timeFromValue.slice(3, 5))
      : null;
    const timeToMinutes = timeToValue
      ? Number(timeToValue.slice(0, 2)) * 60 + Number(timeToValue.slice(3, 5))
      : null;
    const filtered = matches.filter((match) => {
      if (availableOnly && resolveOpenMatchAvailableSlots(match) <= 0) return false;
      if (dateValue) {
        const startAt = parseOpenMatchDateTimeValue(match.startAt);
        if (!startAt) return false;
        const startDateKey = `${startAt.getFullYear()}-${String(startAt.getMonth() + 1).padStart(2, "0")}-${String(startAt.getDate()).padStart(2, "0")}`;
        if (startDateKey !== dateValue) return false;
      }
      if (clubValue && String(match.clubId || "") !== clubValue) return false;
      if (cityValue && match.city !== cityValue) return false;
      if (categoryValue && !match.categories.includes(categoryValue)) return false;
      if (genderValue && match.genderPreference !== genderValue) return false;
      const startMinutes = resolveOpenMatchStartMinutes(match);
      if (timeFromMinutes !== null || timeToMinutes !== null) {
        if (startMinutes === null) return false;
        if (
          timeFromMinutes !== null &&
          timeToMinutes !== null &&
          timeFromMinutes > timeToMinutes
        ) {
          const inWrappedRange =
            startMinutes >= timeFromMinutes || startMinutes <= timeToMinutes;
          if (!inWrappedRange) return false;
        } else {
          if (timeFromMinutes !== null && startMinutes < timeFromMinutes) return false;
          if (timeToMinutes !== null && startMinutes > timeToMinutes) return false;
        }
      }
      if (!normalizedSearch) return true;
      const haystack = [
        match.clubName,
        match.city,
        match.state,
        match.organizer.name,
        formatOpenMatchCategories(match.categories),
      ]
        .join(" ");
      const normalizedHaystack = normalizeSearchText(haystack);
      return normalizedHaystack.includes(normalizedSearch);
    });

    const sorted = sortOpenMatches(filtered);
    if (sortValue === "upcoming") return sorted;
    if (sortValue === "recent") {
      return [...sorted].sort((left, right) => {
        const leftCreated = new Date(String(left.createdAt || "")).getTime();
        const rightCreated = new Date(String(right.createdAt || "")).getTime();
        if (Number.isFinite(leftCreated) && Number.isFinite(rightCreated) && leftCreated !== rightCreated) {
          return rightCreated - leftCreated;
        }
        return sorted.indexOf(left) - sorted.indexOf(right);
      });
    }
    if (sortValue === "price") {
      return [...sorted].sort(
        (left, right) =>
          resolveOpenMatchLowestPerPlayerCents(left) -
          resolveOpenMatchLowestPerPlayerCents(right),
      );
    }
    if (sortValue === "spots") {
      return [...sorted].sort(
        (left, right) =>
          resolveOpenMatchAvailableSlots(right) - resolveOpenMatchAvailableSlots(left),
      );
    }
    return sorted;
  }, [
    availableOnly,
    categoryValue,
    clubValue,
    cityValue,
    dateValue,
    genderValue,
    matches,
    searchValue,
    sortValue,
    timeFromValue,
    timeToValue,
  ]);

  async function handleCreateMatch(payload: CreateOpenMatchPayload) {
    if (!token) {
      throw new Error(texts.actions.createLogin);
    }
    await createMatch(token, payload);
    await loadMatches();
    setShowCreatePanel(false);
  }

  async function handleUpdateMatch(payload: CreateOpenMatchPayload) {
    if (!token || !editingMatch) {
      throw new Error(texts.actions.createLogin);
    }
    const response = await updateMatch(token, editingMatch.id, payload);
    if (response.match) {
      setMatches((current) =>
        current.map((item) =>
          Number(item.id) === Number(response.match.id)
            ? response.match
            : item,
        ),
      );
    } else {
      await loadMatches();
    }
    setEditingMatch(null);
  }

  async function handleCancelMatch() {
    if (!token || !editingMatch) {
      throw new Error(texts.actions.createLogin);
    }
    if (!window.confirm(texts.form.cancelMatchConfirm)) return;
    await cancelMatch(token, editingMatch.id);
    await loadMatches();
    setEditingMatch(null);
    setToastMessage(texts.form.cancelMatchSuccess);
  }

  async function handleCancelListedMatch(match: OpenMatchItem) {
    if (!token) {
      setToastMessage(texts.actions.createLogin);
      return;
    }
    if (!window.confirm(texts.form.cancelMatchConfirm)) return;
    setParticipantActionId(match.id);
    try {
      const response = await cancelMatch(token, match.id);
      if (response.match) {
        setMatches((current) =>
          current.map((item) =>
            Number(item.id) === Number(response.match.id)
              ? response.match
              : item,
          ),
        );
      } else {
        await loadMatches();
      }
      setToastMessage(texts.form.cancelMatchSuccess);
    } catch (err) {
      setToastMessage(
        err instanceof Error ? err.message : texts.form.cancelMatchError,
      );
    } finally {
      setParticipantActionId(null);
    }
  }

  async function handleAcceptInvite(match: OpenMatchItem) {
    if (!token) {
      setToastMessage(texts.actions.createLogin);
      return;
    }
    setParticipantActionId(Number(match.id));
    try {
      const response = await requestJoinMatch(token, match.id);
      if (response.match) {
        setMatches((current) =>
          current.map((item) =>
            Number(item.id) === Number(response.match.id)
              ? response.match
              : item,
          ),
        );
      }
      setToastMessage(texts.detail.joinConfirmed);
    } catch (err) {
      setToastMessage(
        err instanceof Error ? err.message : texts.detail.joinError,
      );
    } finally {
      setParticipantActionId(null);
    }
  }

  async function handleConfirmMatch(match: OpenMatchItem) {
    if (!token) return;
    setParticipantActionId(match.id);
    try {
      const response = await confirmMatch(token, match.id);
      setMatches((current) =>
        current.map((item) =>
          item.id === response.match.id ? response.match : item,
        ),
      );
      setToastMessage(texts.detail.confirmMatchSuccess);
    } catch (err) {
      setToastMessage(
        err instanceof Error ? err.message : texts.detail.confirmMatchError,
      );
    } finally {
      setParticipantActionId(null);
    }
  }

  async function handleDeclineInvite(match: OpenMatchItem) {
    if (!token) {
      setToastMessage(texts.actions.createLogin);
      return;
    }
    setParticipantActionId(Number(match.id));
    try {
      const response = await cancelMyMatchParticipant(token, match.id);
      if (response.match) {
        setMatches((current) =>
          current.map((item) =>
            Number(item.id) === Number(response.match.id)
              ? response.match
              : item,
          ),
        );
      }
      setToastMessage(texts.detail.declineInviteSuccess);
    } catch (err) {
      setToastMessage(
        err instanceof Error ? err.message : texts.detail.cancelParticipantError,
      );
    } finally {
      setParticipantActionId(null);
    }
  }

  async function handleReviewParticipant(
    participantId: number,
    status: "confirmed" | "declined" | "canceled",
  ) {
    if (!token || !editingMatch) {
      throw new Error(texts.actions.createLogin);
    }
    setParticipantActionId(participantId);
    try {
      const response = await reviewMatchParticipant(
        token,
        editingMatch.id,
        participantId,
        status,
      );
      if (response.match) {
        setEditingMatch(response.match);
        setMatches((current) =>
          current.map((item) =>
            Number(item.id) === Number(response.match.id)
              ? response.match
              : item,
          ),
        );
      }
      setToastMessage(texts.detail.reviewSuccess);
    } catch (err) {
      setToastMessage(
        err instanceof Error ? err.message : texts.detail.reviewError,
      );
    } finally {
      setParticipantActionId(null);
    }
  }

  async function handleAddParticipant(
    match: OpenMatchItem,
    payload: OpenMatchInitialParticipant,
  ) {
    if (!token) {
      throw new Error(texts.actions.createLogin);
    }
    setParticipantModalSaving(true);
    try {
      const response = await addMatchParticipant(token, match.id, payload);
      if (response.match) {
        setMatches((current) =>
          current.map((item) =>
            Number(item.id) === Number(response.match.id)
              ? response.match
              : item,
          ),
        );
      } else {
        await loadMatches();
      }
      setParticipantModalMatch(null);
      setToastMessage(texts.detail.addParticipantSuccess);
    } catch (err) {
      setToastMessage(
        err instanceof Error ? err.message : texts.detail.addParticipantError,
      );
    } finally {
      setParticipantModalSaving(false);
    }
  }

  async function handleShare(match: OpenMatchItem) {
    if (match.status === "canceled") return;
    const shareUrl = buildOpenMatchShareUrl(match.id);
    try {
      await navigator.clipboard.writeText(shareUrl);
      setToastMessage(siteTexts.profile.user.dashboard.share.copied);
    } catch (_err) {
      setToastMessage(shareUrl);
    }
  }

  return (
    <AppLayout
      title={<span className="matches-page-title">{texts.title}</span>}
      subtitle={texts.subtitle}
      headerRight={
        user ? (
          <Button type="button" onClick={() => setShowCreatePanel(true)}>
            <span className="material-symbols-outlined" aria-hidden="true">
              add_circle
            </span>
            {texts.actions.create}
          </Button>
        ) : (
          <Button href={buildLoginHref("#/partidas")}>
            <span className="material-symbols-outlined" aria-hidden="true">
              add_circle
            </span>
            {texts.actions.create}
          </Button>
        )
      }
    >
      <div className="matches-page">
        <MatchFiltersBar
          searchValue={searchValue}
          dateValue={dateValue}
          clubValue={clubValue}
          cityValue={cityValue}
          categoryValue={categoryValue}
          genderValue={genderValue}
          timeFromValue={timeFromValue}
          timeToValue={timeToValue}
          availableOnly={availableOnly}
          clubOptions={clubOptions}
          cityOptions={cityOptions}
          categoryOptions={categoryOptions()}
          genderOptions={genderOptions}
          onSearchChange={setSearchValue}
          onDateChange={setDateValue}
          onClubChange={setClubValue}
          onCityChange={setCityValue}
          onCategoryChange={setCategoryValue}
          onGenderChange={setGenderValue}
          onTimeFromChange={setTimeFromValue}
          onTimeToChange={setTimeToValue}
          onAvailableOnlyChange={setAvailableOnly}
          onClear={() => {
            setSearchValue("");
            setDateValue("");
            setClubValue("");
            setCityValue("");
            setCategoryValue("");
            setGenderValue("");
            setTimeFromValue("");
            setTimeToValue("");
            setAvailableOnly(false);
          }}
        />

        {loading ? <LoadingPage label={siteTexts.common.loading} /> : null}

        {!loading && loadError ? (
          <EmptyState
            icon={<span className="material-symbols-outlined">error</span>}
            title={texts.loadError}
            action={
              <Button type="button" onClick={() => void loadMatches()}>
                {texts.actions.retry}
              </Button>
            }
          />
        ) : null}

        {!loading && !loadError ? (
          visibleMatches.length > 0 ? (
            <>
              <section className="matches-results-bar" aria-label={texts.results.title}>
                <div className="matches-results-bar__copy">
                  <div className="matches-results-bar__title-wrap">
                    <span
                      className="matches-results-bar__icon material-symbols-outlined"
                      aria-hidden="true"
                    >
                      list_alt
                    </span>
                    <h2>{texts.results.title}</h2>
                  </div>
                  <span className="matches-results-bar__count">
                    {visibleMatches.length}{" "}
                    {visibleMatches.length === 1
                      ? texts.results.countSingular
                      : texts.results.countPlural}
                  </span>
                </div>
                <label className="matches-results-bar__sort">
                  <span className="matches-results-bar__sort-label">
                    <span className="material-symbols-outlined" aria-hidden="true">
                      swap_vert
                    </span>
                    {texts.results.sortLabel}
                  </span>
                  <div className="matches-results-bar__sort-control">
                    <span className="matches-results-bar__sort-value">
                      {selectedSortOption?.label}
                    </span>
                    <select
                      value={sortValue}
                      onChange={(event) => setSortValue(event.target.value)}
                    >
                      {sortOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <span
                      className="matches-results-bar__sort-chevron material-symbols-outlined"
                      aria-hidden="true"
                    >
                      expand_more
                    </span>
                  </div>
                </label>
              </section>

              <section className="matches-page__grid">
              {visibleMatches.map((match) => (
                <MatchCard
                  key={match.id}
                  match={match}
                  club={match.clubId ? clubsById.get(match.clubId) || null : null}
                  onShare={handleShare}
                  canEdit={Boolean(user && user.id === match.organizer.id)}
                  onEdit={(selectedMatch) => setEditingMatch(selectedMatch)}
                  onAddParticipant={(selectedMatch) =>
                    setParticipantModalMatch(selectedMatch)
                  }
                  onAcceptInvite={(selectedMatch) =>
                    void handleAcceptInvite(selectedMatch)
                  }
                  onDeclineInvite={(selectedMatch) =>
                    void handleDeclineInvite(selectedMatch)
                  }
                  inviteActionRunning={participantActionId === match.id}
                  onConfirmMatch={(selectedMatch) =>
                    void handleConfirmMatch(selectedMatch)
                  }
                  confirmActionRunning={participantActionId === match.id}
                  onCancelMatch={(selectedMatch) =>
                    void handleCancelListedMatch(selectedMatch)
                  }
                  cancelActionRunning={participantActionId === match.id}
                />
              ))}
              </section>
            </>
          ) : (
            <EmptyState
              iconName="sports_tennis"
              title={texts.emptyTitle}
              description={texts.emptyDescription}
            />
          )
        ) : null}
      </div>

      <MatchCreatePanel
        open={showCreatePanel || Boolean(editingMatch)}
        clubs={clubs}
        loadingClubs={loadingClubs}
        mode={editingMatch ? "edit" : "create"}
        initialMatch={editingMatch}
        prefillClubId={routeState.prefilledClubId}
        defaultOrganizerName={user?.name || ""}
        onSubmit={editingMatch ? handleUpdateMatch : handleCreateMatch}
        onCancelMatch={editingMatch ? handleCancelMatch : undefined}
        onAddParticipant={editingMatch ? handleAddParticipant : undefined}
        onReviewParticipant={
          editingMatch ? handleReviewParticipant : undefined
        }
        reviewingParticipantId={participantActionId}
        onClose={() => {
          setShowCreatePanel(false);
          setEditingMatch(null);
        }}
      />

      <MatchParticipantModal
        open={Boolean(participantModalMatch)}
        match={participantModalMatch}
        saving={participantModalSaving}
        onSubmit={handleAddParticipant}
        onClose={() => setParticipantModalMatch(null)}
      />

      {toastMessage ? (
        <Toast message={toastMessage} onClose={() => setToastMessage("")} />
      ) : null}
    </AppLayout>
  );
}
